import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Avatar } from '@/components/redesign/bits'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { useStaff } from '@/features/team/api'
import { downloadFile, http } from '@/lib/api/client'
import { errorMessage } from '@/lib/api/errors'
import type { AuditEntry, Paginated } from '@/lib/api/types'
import { numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'

/** The log filters by the start of an action's name. */
const AREAS = [
  { value: '', label: 'All' },
  { value: 'auth.', label: 'Sign-ins' },
  { value: 'ticket.', label: 'Support' },
  { value: 'customer', label: 'Customers' },
  { value: 'discount.', label: 'Discounts' },
  { value: 'settings.', label: 'Settings' },
  { value: 'staff.', label: 'Team' },
]

const AREA_LABEL: [string, string][] = [
  ['auth.', 'Sign-in'],
  ['ticket.', 'Support'],
  ['customer', 'Customers'],
  ['discount.', 'Discounts'],
  ['settings.', 'Settings'],
  ['staff.', 'Team'],
  ['profile.', 'Profile'],
  ['help.', 'Help center'],
  ['email.', 'Emails'],
  ['referral_payout.', 'Referrals'],
  ['billing_event.', 'Billing'],
  ['sales.', 'Revenue'],
  ['subscriptions.', 'Subscriptions'],
]
const areaOf = (action: string) => AREA_LABEL.find(([p]) => action.startsWith(p))?.[1] ?? 'Other'

type Props = Record<string, unknown>
const str = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '')

/** What happened, as the rest of a sentence after the person's name. */
const SAID: Record<string, (p: Props) => string> = {
  'auth.signed_in': () => 'signed in',
  'auth.signed_out': () => 'signed out',
  'auth.sign_in_failed': () => 'couldn’t sign in: wrong email or password',
  'auth.two_factor_failed': () => 'entered a wrong two-step code',
  'auth.two_factor_set_up': () => 'set up two-step verification',
  'ticket.viewed': (p) => `opened ticket ${p.number ? `#${str(p.number)}` : ''}`.trim(),
  'ticket.replied': (p) => `replied on ticket ${p.number ? `#${str(p.number)}` : ''}`.trim(),
  'ticket.assigned': (p) => `gave a ticket ${p.number ? `(#${str(p.number)}) ` : ''}to someone`,
  'ticket.template_created': () => 'added a reply template',
  'ticket.template_updated': () => 'changed a reply template',
  'ticket.template_deleted': () => 'deleted a reply template',
  'customer.viewed': (p) => `opened ${str(p.name) || 'a customer'}`,
  'customer.plan_changed': () => 'changed a customer’s plan',
  'customer.plan_cancelled': () => 'cancelled a customer’s plan',
  'customer.plan_resumed': () => 'resumed a customer’s plan',
  'customer.plan_kept': () => 'kept a customer’s plan from ending',
  'customer.complimentary_plan': () => 'gave a customer a free plan',
  'customer.complimentary_addons': () => 'gave a customer free add-ons',
  'customer.checkout_link_sent': () => 'sent a customer a checkout link',
  'customer.email_change_sent': () => 'started changing a customer’s owner email',
  'customer.internal_account': () => 'marked an account as internal',
  'customer.crm_updated': () => 'updated a customer’s details',
  'customer.note_added': () => 'added a note to a customer',
  'customer.note_deleted': () => 'deleted a note on a customer',
  'customers.searched': (p) => `searched customers for “${str(p.search)}”`,
  'customers.exported': () => 'exported customers as a CSV',
  'discount.created': (p) => `made the discount code ${str(p.code)}`,
  'discount.removed': (p) => `ended the discount code ${str(p.code)}`.trim(),
  'discount.synced': () => 'synced discount codes from Lemon Squeezy',
  'settings.updated': (p) => `changed the ${str(p.key).replace(/_/g, ' ')} setting`,
  'staff.invited': (p) => `invited ${str(p.email) || str(p.name) || 'someone'}`,
  'staff.invitation_revoked': () => 'revoked an invitation',
  'staff.created': (p) => `added ${str(p.name) || 'a teammate'}`,
  'staff.role_changed': (p) =>
    `changed ${str(p.name) || 'a teammate'}’s role${p.from ? ` from ${str(p.from)}` : ''}${p.to ? ` to ${str(p.to)}` : ''}`,
  'staff.disabled': (p) => `turned off ${str(p.name) || 'a teammate'}’s access`,
  'staff.enabled': (p) => `turned on ${str(p.name) || 'a teammate'}’s access`,
  'staff.two_factor_reset': (p) => `reset ${str(p.name) || 'a teammate'}’s two-step verification`,
  'profile.name_changed': () => 'changed their name',
  'profile.password_changed': () => 'changed their password',
  'help.article_created': () => 'wrote a help article',
  'help.article_deleted': () => 'deleted a help article',
  'help.category_created': () => 'added a help section',
  'help.category_updated': () => 'changed a help section',
  'help.category_deleted': () => 'deleted a help section',
  'email.created': () => 'added a lifecycle email',
  'email.deleted': () => 'deleted a lifecycle email',
  'referral_payout.paid': () => 'marked a referral payout as sent',
  'referral_payout.cancelled': () => 'cancelled a referral payout',
  'billing_event.retried': () => 'ran a billing event again',
  'sales.viewed': () => 'opened Revenue',
  'subscriptions.searched': (p) => `searched subscriptions for “${str(p.search)}”`,
}

/** Properties already said in the sentence, or shown as the change below it. */
const SHOWN = new Set(['name', 'number', 'code', 'key', 'before', 'after', 'search', 'from', 'to'])

function details(p: Props | null) {
  if (!p) return ''
  return Object.entries(p)
    .filter(([k]) => !SHOWN.has(k))
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' · ')
}

/** A setting's fields that changed, old and new. */
function changes(p: Props | null): [string, string, string][] {
  const before = p?.before
  const after = p?.after
  if (typeof before !== 'object' || typeof after !== 'object' || before === null || after === null) {
    return []
  }
  const b = before as Props
  const a = after as Props
  return Object.keys(a)
    .filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]))
    .map((k) => [k.replace(/_/g, ' '), JSON.stringify(b[k] ?? ''), JSON.stringify(a[k])])
}

const dayFmt = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })

function dayLabel(iso: string, now: number) {
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((today.getTime() - new Date(iso).setHours(0, 0, 0, 0)) / 86_400_000)
  return diff <= 0 ? 'Today' : diff === 1 ? 'Yesterday' : dayFmt.format(new Date(iso))
}

/**
 * Everything staff did in the control panel, newest first and grouped by day (redesign of
 * 2026-10-16). Entries can't be changed or deleted.
 */
export function AuditLogPage() {
  const now = useNow()
  const staff = useStaff()
  const me = useMe()
  const [staffId, setStaffId] = useState('')
  const [action, setAction] = useState('')
  const [typed, setTyped] = useState('')
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  // Search as you type, a moment after the last key.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(typed.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [typed])
  const filters = { staff_id: staffId, action, q, from, to }
  const log = useQuery({
    queryKey: ['audit-log', filters, page],
    queryFn: () => http.get<Paginated<AuditEntry>>('/audit-log', { ...filters, page }),
    placeholderData: keepPreviousData,
  })
  const canExport = me.data?.role === 'owner' || me.data?.role === 'admin'
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadFile('/audit-log/export', filters)
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setExporting(false)
    }
  }

  const days: { day: string; items: AuditEntry[] }[] = []
  for (const e of log.data?.data ?? []) {
    const day = dayLabel(e.created_at, now)
    const last = days.at(-1)
    if (last?.day === day) last.items.push(e)
    else days.push({ day, items: [e] })
  }

  return (
    <>
      <PageTitle
        title="Audit log"
        description="Everything staff did in the control panel, including every sign-in and every customer page they opened. Entries can’t be edited or deleted."
        actions={
          canExport && (
            <button type="button" className="btn" disabled={exporting} onClick={() => void exportCsv()}>
              <Download aria-hidden="true" />
              Export CSV
            </button>
          )
        }
      />
      <div className="fbar">
        <SearchField
          label="Search the audit log"
          placeholder="Search actions, details, IDs or IP"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <NativeSelect
          aria-label="Staff member"
          value={staffId}
          onChange={(e) => {
            setStaffId(e.target.value)
            setPage(1)
          }}
          className="h-9 w-52 text-[13px]"
        >
          <option value="">Everyone</option>
          {(staff.data?.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </NativeSelect>
        <Segmented
          label="What"
          value={action}
          options={AREAS}
          onChange={(v) => {
            setAction(v)
            setPage(1)
          }}
        />
        <label className="inline-flex items-center gap-2 text-[13px] text-ink-muted">
          From
          <Input
            type="date"
            aria-label="From"
            value={from}
            max={to || undefined}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(1)
            }}
            className="h-9 w-[150px] text-[13px]"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-[13px] text-ink-muted">
          To
          <Input
            type="date"
            aria-label="To"
            value={to}
            min={from || undefined}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(1)
            }}
            className="h-9 w-[150px] text-[13px]"
          />
        </label>
      </div>

      {log.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : log.isError ? (
        <div className="card">
          <ErrorState error={log.error} onRetry={() => void log.refetch()} />
        </div>
      ) : log.data.data.length === 0 ? (
        <div className="card">
          <EmptyState title="Nothing recorded" description="No entries match these filters." />
        </div>
      ) : (
        <div className="card pad">
          {days.map(({ day, items }) => (
            <section key={day} aria-label={day}>
              <h2 className="day-h">{day}</h2>
              <ol className="tl" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {items.map((e) => {
                  const said = SAID[e.action]?.(e.properties ?? {}) ?? e.action
                  const more = details(e.properties)
                  const diff = e.action === 'settings.updated' ? changes(e.properties) : []
                  return (
                    <li key={e.id} className="fe">
                      <Avatar name={e.staff?.name} size="sm" />
                      <div className="fe-t">
                        <b>{e.staff?.name ?? 'Someone'}</b> {said}
                        <small>
                          <span className="chip" style={{ height: 20, fontSize: 11 }}>
                            {areaOf(e.action)}
                          </span>
                          {e.workspace_id && <Link to={`/customers/${e.workspace_id}`}>Open customer</Link>}
                          {more && <span style={{ wordBreak: 'break-word' }}>{more}</span>}
                          {e.ip && <span className="mono">from {e.ip}</span>}
                        </small>
                        {diff.length > 0 && (
                          <div className="diff">
                            {diff.map(([k, b, a]) => (
                              <div key={k}>
                                {k}: <span className="d">{b}</span> <span className="a">{a}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <time className="fe-time" dateTime={e.created_at}>
                        {timeFmt.format(new Date(e.created_at))}
                      </time>
                    </li>
                  )
                })}
              </ol>
            </section>
          ))}
          {log.data.meta.last_page > 1 && (
            <div className="t-foot" style={{ marginTop: 8 }}>
              <span>
                Page {log.data.meta.current_page} of {log.data.meta.last_page} ·{' '}
                {numberFmt.format(log.data.meta.total)} entries
              </span>
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= log.data.meta.last_page}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </>
  )
}
